import { useQueryClient } from '@tanstack/react-query';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { useAppMutation } from '../use-app-mutation.hook';
import { useSelectedCompany } from '../use-selected-company.hook';
import { DATA_ROOM_ACCESS_INVALIDATION_KEYS } from './access-invalidation.const';
import { DataRoomAccessRow, PermissionChange } from './space.types';

type SaveVariables = { changes: PermissionChange[]; id: string };

/**
 * The Save half of Manage access: every changed permission goes in one request, so the modal
 * cannot half-apply. Row actions are the other half and have their own hook — they take effect
 * the moment they are confirmed.
 *
 * The refetch is awaited, which is why this invalidates through the client rather than through
 * `useInvalidateQueries`: the modal drops its local edits when the save succeeds, and a success
 * that lands before the list has refreshed drops them onto the rows still in the cache. The
 * control then showed the old permission for the length of the refetch and flipped back — a
 * visible redraw of the row somebody had just saved. Returning the promise holds the mutation
 * open until the rows the modal reads carry the saved value.
 */
export const useSaveAccessPermissions = () => {
  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id ?? '';
  const queryClient = useQueryClient();

  const { isPending, mutate: savePermissions } = useAppMutation<
    DataRoomAccessRow[],
    unknown,
    SaveVariables
  >([QueryKey.SAVE_ACCESS_PERMISSIONS, companyId], {
    mutationFn: ({ id, changes }) =>
      ApiService.patch(
        { endpoint: BackendRoute.DATA_ROOMS, routePath: [id, 'access'] },
        { body: { changes } },
      ),
    onSuccess: () =>
      Promise.all(
        DATA_ROOM_ACCESS_INVALIDATION_KEYS.map((key) =>
          queryClient.invalidateQueries({ exact: false, queryKey: [key] }),
        ),
      ),
  });

  return { isPending, savePermissions };
};
